/**
 * BevisOversikt - Oversikt over alle bevis for en sak
 * Filtrering, sortering og visning av beviskjede
 */

import React, { useState, useEffect } from 'react';
import crmApi from '../api/crm';
import BevisDialog from './BevisDialog';
import Beviskjede from './Beviskjede';
import './BevisOversikt.css';

export default function BevisOversikt({ sakId }) {
  const [bevis, setBevis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kategorier, setKategorier] = useState([]);

  // UI state
  const [visBevisDialog, setVisBevisDialog] = useState(false);
  const [redigerBevis, setRedigerBevis] = useState(null);
  const [visBeviskjede, setVisBeviskjede] = useState(null);
  const [visEksport, setVisEksport] = useState(false);

  // Filtre
  const [filter, setFilter] = useState({
    type: '',
    verdi: '',
    status: '',
    kategori: '',
    søk: ''
  });

  // Sortering
  const [sortering, setSortering] = useState({
    felt: 'created_at',
    retning: 'desc'
  });

  // Valgte bevis for eksport
  const [valgteBevis, setValgteBevis] = useState([]);

  useEffect(() => {
    if (sakId) {
      lastBevis();
      lastKategorier();
    }
  }, [sakId, filter, sortering]);

  const lastBevis = async () => {
    try {
      setLoading(true);

      // Bygg query params
      const params = new URLSearchParams({ sak_id: sakId });
      if (filter.type) params.append('type', filter.type);
      if (filter.verdi) params.append('verdi', filter.verdi);
      if (filter.status) params.append('status', filter.status);
      if (filter.kategori) params.append('kategori_id', filter.kategori);

      const data = await crmApi.request(`/bevis?${params.toString()}`);

      let bevisList = data.bevis || [];

      // Søkefiltrering (lokalt)
      if (filter.søk) {
        const søkeord = filter.søk.toLowerCase();
        bevisList = bevisList.filter(b =>
          b.bevis_for?.toLowerCase().includes(søkeord) ||
          b.bevis_type_detaljer?.toLowerCase().includes(søkeord) ||
          b.filename?.toLowerCase().includes(søkeord) ||
          b.tags?.some(tag => tag.toLowerCase().includes(søkeord))
        );
      }

      // Sortering
      bevisList.sort((a, b) => {
        let aVal = a[sortering.felt];
        let bVal = b[sortering.felt];

        // Håndter spesialtilfeller
        if (sortering.felt === 'bevisverdi') {
          const verdier = { kritisk: 4, høy: 3, normal: 2, støttende: 1 };
          aVal = verdier[a.bevisverdi] || 0;
          bVal = verdier[b.bevisverdi] || 0;
        }

        if (aVal < bVal) return sortering.retning === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortering.retning === 'asc' ? 1 : -1;
        return 0;
      });

      setBevis(bevisList);
    } catch (error) {
      console.error('Feil ved lasting av bevis:', error);
    } finally {
      setLoading(false);
    }
  };

  const lastKategorier = async () => {
    try {
      const data = await crmApi.request(`/bevis/kategorier?sak_id=${sakId}`);
      setKategorier(data.kategorier || []);
    } catch (error) {
      console.error('Feil ved lasting av kategorier:', error);
    }
  };

  const slettBevis = async (bevisId) => {
    if (!window.confirm('Er du sikker på at du vil slette dette beviset?\n\nBeviskjeden vil bli logget før sletting (audit trail).')) {
      return;
    }

    try {
      await crmApi.request(`/bevis/${bevisId}`, {
        method: 'DELETE'
      });

      alert('✓ Bevis slettet (beviskjede logget)');
      lastBevis();
    } catch (error) {
      console.error('Feil ved sletting av bevis:', error);
      alert('Kunne ikke slette bevis');
    }
  };

  const oppdaterStatus = async (bevisId, nyStatus) => {
    try {
      await crmApi.request(`/bevis/${bevisId}`, {
        method: 'PUT',
        body: JSON.stringify({ status: nyStatus })
      });

      lastBevis();
    } catch (error) {
      console.error('Feil ved oppdatering av status:', error);
    }
  };

  const toggleValgBevis = (bevisId) => {
    if (valgteBevis.includes(bevisId)) {
      setValgteBevis(valgteBevis.filter(id => id !== bevisId));
    } else {
      setValgteBevis([...valgteBevis, bevisId]);
    }
  };

  const eksporterBevisfortegnelse = async () => {
    if (valgteBevis.length === 0) {
      alert('Velg minst ett bevis for å eksportere');
      return;
    }

    try {
      const data = await crmApi.request('/bevis/eksporter', {
        method: 'POST',
        body: JSON.stringify({
          sak_id: sakId,
          bevis_ids: valgteBevis,
          format: 'pdf',
          inkluder_beviskjede: true
        })
      });

      alert(`✓ Bevisfortegnelse generert: ${data.filnavn}\n\nKlar for nedlasting`);
      // TODO: Implementer nedlasting
    } catch (error) {
      console.error('Feil ved eksport:', error);
      alert('Kunne ikke eksportere bevisfortegnelse');
    }
  };

  const getBevisTypeEmoji = (type) => {
    const emojis = {
      materielt: '🔍',
      formelt: '📜',
      teknisk: '🔧',
      vitne: '👤',
      dokument: '📄',
      elektronisk: '💻',
      annet: '📋'
    };
    return emojis[type] || '📋';
  };

  const getBevisverdiFarge = (verdi) => {
    const farger = {
      kritisk: '#d32f2f',
      høy: '#f57c00',
      normal: '#fbc02d',
      støttende: '#388e3c'
    };
    return farger[verdi] || '#757575';
  };

  const getStatusFarge = (status) => {
    const farger = {
      identifisert: '#9e9e9e',
      sikret: '#2196f3',
      verifisert: '#4caf50',
      presentert: '#ff9800',
      godkjent: '#8bc34a',
      avvist: '#f44336'
    };
    return farger[status] || '#757575';
  };

  // Statistikk
  const stats = {
    totalt: bevis.length,
    kritisk: bevis.filter(b => b.bevisverdi === 'kritisk').length,
    høy: bevis.filter(b => b.bevisverdi === 'høy').length,
    verifisert: bevis.filter(b => b.status === 'verifisert').length,
    presentert: bevis.filter(b => b.status === 'presentert' || b.status === 'godkjent').length
  };

  if (loading) {
    return <div className="bevis-loading">🔍 Laster bevis...</div>;
  }

  return (
    <div className="bevis-oversikt-container">
      {/* Header med statistikk */}
      <div className="bevis-header">
        <div>
          <h2>🔒 Dokumentbevis</h2>
          <div className="bevis-stats">
            <span className="stat-item">Totalt: <strong>{stats.totalt}</strong></span>
            <span className="stat-item kritisk">Kritiske: <strong>{stats.kritisk}</strong></span>
            <span className="stat-item høy">Høy verdi: <strong>{stats.høy}</strong></span>
            <span className="stat-item verifisert">Verifisert: <strong>{stats.verifisert}</strong></span>
            <span className="stat-item presentert">Presentert: <strong>{stats.presentert}</strong></span>
          </div>
        </div>

        <div className="bevis-header-actions">
          <button
            className="btn-secondary"
            onClick={() => window.open(`${crmApi.baseUrl}/api/bevis/pdf/sak/${sakId}`, '_blank')}
            title="Eksporter bevisfortegnelse som PDF"
          >
            📄 Bevisfortegnelse PDF
          </button>
          {valgteBevis.length > 0 && (
            <button
              className="btn-eksport"
              onClick={() => setVisEksport(true)}
            >
              📄 Eksporter ({valgteBevis.length})
            </button>
          )}
          <button className="btn-primary" onClick={() => setVisBevisDialog(true)}>
            ➕ Nytt bevis
          </button>
        </div>
      </div>

      {/* Filter og søk */}
      <div className="bevis-filter">
        <input
          type="text"
          className="søk-input"
          placeholder="🔍 Søk i bevis..."
          value={filter.søk}
          onChange={(e) => setFilter({...filter, søk: e.target.value})}
        />

        <select
          value={filter.type}
          onChange={(e) => setFilter({...filter, type: e.target.value})}
        >
          <option value="">Alle typer</option>
          <option value="materielt">🔍 Materielt</option>
          <option value="formelt">📜 Formelt</option>
          <option value="teknisk">🔧 Teknisk</option>
          <option value="vitne">👤 Vitne</option>
          <option value="dokument">📄 Dokument</option>
          <option value="elektronisk">💻 Elektronisk</option>
          <option value="annet">📋 Annet</option>
        </select>

        <select
          value={filter.verdi}
          onChange={(e) => setFilter({...filter, verdi: e.target.value})}
        >
          <option value="">Alle verdier</option>
          <option value="kritisk">🔴 Kritisk</option>
          <option value="høy">🟠 Høy</option>
          <option value="normal">🟡 Normal</option>
          <option value="støttende">🟢 Støttende</option>
        </select>

        <select
          value={filter.status}
          onChange={(e) => setFilter({...filter, status: e.target.value})}
        >
          <option value="">Alle statuser</option>
          <option value="identifisert">Identifisert</option>
          <option value="sikret">Sikret</option>
          <option value="verifisert">Verifisert</option>
          <option value="presentert">Presentert</option>
          <option value="godkjent">Godkjent</option>
          <option value="avvist">Avvist</option>
        </select>

        {kategorier.length > 0 && (
          <select
            value={filter.kategori}
            onChange={(e) => setFilter({...filter, kategori: e.target.value})}
          >
            <option value="">Alle kategorier</option>
            {kategorier.map((kat) => (
              <option key={kat.id} value={kat.id}>
                {kat.navn}
              </option>
            ))}
          </select>
        )}

        <select
          value={`${sortering.felt}-${sortering.retning}`}
          onChange={(e) => {
            const [felt, retning] = e.target.value.split('-');
            setSortering({ felt, retning });
          }}
        >
          <option value="created_at-desc">Nyeste først</option>
          <option value="created_at-asc">Eldste først</option>
          <option value="bevisverdi-desc">Høyeste verdi først</option>
          <option value="bevisverdi-asc">Laveste verdi først</option>
          <option value="status-asc">Status A-Å</option>
        </select>

        {(filter.type || filter.verdi || filter.status || filter.kategori || filter.søk) && (
          <button
            className="btn-nullstill"
            onClick={() => setFilter({ type: '', verdi: '', status: '', kategori: '', søk: '' })}
          >
            ✕ Nullstill filtre
          </button>
        )}
      </div>

      {/* Bevisliste */}
      {bevis.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">🔒</div>
          <h3>Ingen bevis registrert</h3>
          <p>Merk dokumenter som bevis for å sikre dem med beviskjede</p>
          <button className="btn-primary" onClick={() => setVisBevisDialog(true)}>
            Opprett første bevis
          </button>
        </div>
      ) : (
        <div className="bevis-grid">
          {bevis.map((b) => (
            <div
              key={b.id}
              className="bevis-card"
              style={{ borderLeft: `4px solid ${getBevisverdiFarge(b.bevisverdi)}` }}
            >
              <div className="bevis-card-header">
                <div className="bevis-type">
                  {getBevisTypeEmoji(b.bevis_type)} {b.bevis_type}
                </div>
                <div className="bevis-actions">
                  <input
                    type="checkbox"
                    checked={valgteBevis.includes(b.id)}
                    onChange={() => toggleValgBevis(b.id)}
                    title="Velg for eksport"
                  />
                  <button
                    onClick={() => { setRedigerBevis(b); setVisBevisDialog(true); }}
                    className="btn-icon"
                    title="Rediger"
                  >
                    ✏️
                  </button>
                  <button
                    onClick={() => setVisBeviskjede(b.id)}
                    className="btn-icon"
                    title="Vis beviskjede"
                  >
                    🔗
                  </button>
                  <button
                    onClick={() => window.open(`${crmApi.baseUrl}/api/bevis/pdf/${b.id}`, '_blank')}
                    className="btn-icon"
                    title="Chain of Custody PDF"
                  >
                    📄
                  </button>
                  <button
                    onClick={() => slettBevis(b.id)}
                    className="btn-icon btn-delete"
                    title="Slett"
                  >
                    🗑️
                  </button>
                </div>
              </div>

              <h3 className="bevis-for">{b.bevis_for}</h3>

              {b.filename && (
                <div className="bevis-dokument">
                  📄 {b.filename}
                  {b.file_size && <span className="file-size"> • {(b.file_size / 1024 / 1024).toFixed(2)} MB</span>}
                </div>
              )}

              {b.bevis_type_detaljer && (
                <p className="bevis-detaljer">{b.bevis_type_detaljer}</p>
              )}

              <div className="bevis-meta">
                <span
                  className="bevisverdi-badge"
                  style={{ backgroundColor: getBevisverdiFarge(b.bevisverdi) }}
                >
                  {b.bevisverdi}
                </span>

                <select
                  value={b.status}
                  onChange={(e) => oppdaterStatus(b.id, e.target.value)}
                  className="status-select"
                  style={{ color: getStatusFarge(b.status) }}
                >
                  <option value="identifisert">Identifisert</option>
                  <option value="sikret">Sikret</option>
                  <option value="verifisert">Verifisert</option>
                  <option value="presentert">Presentert</option>
                  <option value="godkjent">Godkjent</option>
                  <option value="avvist">Avvist</option>
                </select>

                {b.sikret_dato && (
                  <span className="meta-dato">
                    🔒 Sikret: {new Date(b.sikret_dato).toLocaleDateString('nb-NO')}
                  </span>
                )}

                {b.verifisert_dato && (
                  <span className="meta-dato">
                    ✓ Verifisert: {new Date(b.verifisert_dato).toLocaleDateString('nb-NO')}
                  </span>
                )}
              </div>

              {b.tags && b.tags.length > 0 && (
                <div className="bevis-tags">
                  {b.tags.map((tag) => (
                    <span key={tag} className="tag">#{tag}</span>
                  ))}
                </div>
              )}

              {b.kategorier && b.kategorier.length > 0 && (
                <div className="bevis-kategorier">
                  {b.kategorier.map((kat) => (
                    <span
                      key={kat.id}
                      className="kategori-badge"
                      style={{ backgroundColor: kat.farge }}
                    >
                      {kat.navn}
                    </span>
                  ))}
                </div>
              )}

              {b.konklusjon && (
                <div className="bevis-konklusjon">
                  <strong>Konklusjon:</strong> {b.konklusjon}
                </div>
              )}

              {b.juridisk_vurdering && (
                <div className="bevis-juridisk">
                  <strong>Juridisk vurdering:</strong> {b.juridisk_vurdering}
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Modaler */}
      {visBevisDialog && (
        <BevisDialog
          sakId={sakId}
          documentId={null}
          documentNavn="Velg dokument"
          eksisterendeBevis={redigerBevis}
          onClose={() => { setVisBevisDialog(false); setRedigerBevis(null); }}
          onSuccess={() => { lastBevis(); }}
        />
      )}

      {visBeviskjede && (
        <Beviskjede
          bevisId={visBeviskjede}
          onClose={() => setVisBeviskjede(null)}
        />
      )}

      {visEksport && (
        <div className="modal-overlay" onClick={() => setVisEksport(false)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h2>📄 Eksporter bevisfortegnelse</h2>
            <p>Du er i ferd med å eksportere {valgteBevis.length} bevis til PDF.</p>
            <p>Bevisfortegnelsen vil inkludere:</p>
            <ul>
              <li>✓ Alle bevisdetaljer</li>
              <li>✓ Beviskjede (Chain of Custody)</li>
              <li>✓ SHA-256 hashes for integritet</li>
              <li>✓ Tidsstempler og signaturer</li>
            </ul>

            <div className="modal-actions">
              <button className="btn-secondary" onClick={() => setVisEksport(false)}>
                Avbryt
              </button>
              <button
                className="btn-primary"
                onClick={() => { eksporterBevisfortegnelse(); setVisEksport(false); }}
              >
                📄 Generer PDF
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
